// Curated map from concept ideas mentioned in Shariq's essays to their
// canonical Shrinkopedia URLs. Every URL below is sitemap-exact against
// shrinkopedia.com/sitemap-index.xml at the time of curation. The
// audit script at scripts/audit-shrinkopedia-urls.mjs re-checks these against
// the live sitemap on every build; add a slug here only if it is present in
// that sitemap.
//
// Editorial framing:
//   Shariq's essays are him speaking as himself. Shrinkopedia is the
//   encyclopedia he medically reviews. These references say "I cover the
//   reference version of X on Shrinkopedia" — not "read more here." First
//   person on the founder site, reference tone on the encyclopedia.
//
// UTM shape:
//   utm_source=shariqrefai
//   utm_medium=network_journey
//   utm_campaign=auto_perspectives_{essay_slug}    (essay pages)
//   utm_campaign=auto_credentialing_{page_slug}    (medical-reviewer etc.)

export interface ShrinkopediaEntry {
  /** Human label shown to readers. */
  label: string;
  /** Canonical path, e.g. "/library/rumination/". Must be sitemap-exact. */
  path: string;
}

/**
 * Per-essay curated Shrinkopedia deep links. Order matters: the first entry
 * in each array is the closest conceptual match and will be the primary
 * reference. Cap at 3 entries per essay (matches the reciprocity spec).
 */
export const ESSAY_SHRINKOPEDIA_LINKS: Record<string, ShrinkopediaEntry[]> = {
  "why-your-mind-feels-loudest-when-trying-to-rest": [
    { label: "the reference entry on rumination", path: "/library/rumination/" },
    { label: "insomnia disorder", path: "/conditions/insomnia-disorder/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "difference-between-a-thought-and-thinking": [
    { label: "rumination", path: "/library/rumination/" },
    { label: "cognitive distortions", path: "/library/cognitive-distortions/" },
    { label: "cognitive behavioral therapy", path: "/treatments/cbt/" },
  ],
  "why-thinking-harder-usually-makes-anxiety-worse": [
    { label: "rumination", path: "/library/rumination/" },
    { label: "cognitive distortions", path: "/library/cognitive-distortions/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "why-your-brain-replays-conversations-long-after-they-end": [
    { label: "rumination", path: "/library/rumination/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "why-high-performers-can-still-feel-anxious": [
    { label: "burnout", path: "/library/burnout/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "why-your-nervous-system-thinks-everything-is-urgent": [
    { label: "hypervigilance", path: "/terms/hypervigilance/" },
    { label: "sympathetic nervous system", path: "/terms/sympathetic-nervous-system/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "why-popular-mental-health-commentary-falls-short-in-the-moment": [
    { label: "panic attack", path: "/symptoms/panic-attack/" },
    { label: "cognitive behavioral therapy", path: "/treatments/cbt/" },
    { label: "the anxiety hub", path: "/hubs/anxiety/" },
  ],
  "what-modern-psychiatry-gets-right": [
    { label: "SSRIs", path: "/treatments/ssris/" },
    { label: "cognitive behavioral therapy", path: "/treatments/cbt/" },
  ],
  // digital-mental-health-needs-more-than-convenience: no clear reference entry
  // in Shrinkopedia today. Left empty.
};

/**
 * Credentialing pages that link to Shrinkopedia's counterpart pages so both
 * sides of the network agree on who reviews the encyclopedia.
 */
export const CREDENTIALING_SHRINKOPEDIA_LINKS: Record<string, ShrinkopediaEntry[]> = {
  "medical-reviewer": [
    { label: "Shrinkopedia author profile", path: "/authors/shariq-refai/" },
    { label: "Shrinkopedia editorial team", path: "/editorial-team/" },
    { label: "Shrinkopedia medical review board", path: "/medical-review-board/" },
  ],
  "medical-review-board": [
    { label: "Shrinkopedia medical review board", path: "/medical-review-board/" },
    { label: "Shrinkopedia editorial team", path: "/editorial-team/" },
  ],
  "founder-of-the-shrink-network": [
    { label: "Shrinkopedia author profile", path: "/authors/shariq-refai/" },
    { label: "Shrinkopedia editorial team", path: "/editorial-team/" },
  ],
};

const SHRINKOPEDIA_URL = "https://shrinkopedia.com";

/**
 * Build a full deep-link URL with the standard reciprocity UTM tuple.
 *   campaignSlug is the essay/page slug (e.g. "perspectives_rumination").
 */
export function shrinkopediaUrl(path: string, campaignSlug: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  const params = new URLSearchParams({
    utm_source: "shariqrefai",
    utm_medium: "network_journey",
    utm_campaign: campaignSlug,
  });
  return `${SHRINKOPEDIA_URL}${clean}?${params.toString()}`;
}
